import md5 from 'js-md5';
import {nanoid} from 'nanoid';
import { UAParser } from 'ua-parser-js';
import TraceKit from 'tracekit'

const uaParser = new UAParser()
const INTERVAL = 1000 * 30

const request = ({
  url,
  data
}) => {
  if (window.navigator.sendBeacon) {
    const result = navigator.sendBeacon(url, JSON.stringify(data));
    if (result) return result
    return fetch(url, {
      method: 'POST',
      body: JSON.stringify(data),
      mode: "no-cors"
    })
  } else {
    return fetch(url, {
      method: 'POST',
      body: JSON.stringify(data),
      mode: "no-cors"
    })
  }
}

class Rum{
  static _instance = null;
  /**
   * Rum 类的构造函数
   * @param {Object} option - 构造函数参数对象
   * @param {string} option.app_id - 应用程序 ID
   * @param {string} option.env - 运行环境
   */
  constructor(option = {}) {
    if (Rum._instance !== null) {
      throw new Error('Rum instance already exists');
    }
    /**
     * 应用程序 ID
     * @type {string}
     */
    this.app_id = ''

    /**
     * 运行环境
     * @type {string}
     */
    this.env = ''

    /**
     * 自定义rum上报通用参数
     * @type {string}
     */
    this.config_etag = ''

    /**
     * 自定义rum上报通用参数
     * @type {string}
     */
    this.user_id = this.getDeviceId()

    /**
     * 日志池子
     */
    this.logPool = new Map()

    /**
     * 重复日志上报时间间隔，单位ms
     */
    this.interval = INTERVAL

    this.init(option)
  }

  static getInstance() {
    if (Rum._instance === null) {
      Rum._instance = new Rum();
    }

    return Rum._instance;
  }

  /**
   * 支持vue.use()
   * @hidden
   * @param Vue
   */
  install(Vue, opt) {
    Vue.prototype.$rum = Rum.getInstance()
    Rum._instance.init(opt)
  }

  /**
   * 初始化参数
   */
  init(option) {
    this.app_id = option.app_id
    this.env = option.env
    this.interval = option.interval || INTERVAL
  }

  /**
   * 获取rum上报地址
   */
  getReportUrl() {
    const {
      rum_app_tag,
      rum_url,
      rum_appkey
    } = {
      production: {
        rum_app_tag: 'devops.rum.us',
        rum_url: 'https://logagent-wf.centurygame.com',
        rum_appkey: 'd3b7c2e58b97b29faa637dc31af8feb6',
      },
      development: {
        rum_app_tag: 'devops.rum.sandbox',
        rum_url: 'https://logagent-sandbox.centurygame.com',
        rum_appkey: '91758f892f6c045cdeb2575ed86c1be9',
      },
      test: {
        rum_app_tag: 'devops.rum.sandbox',
        rum_url: 'https://logagent-sandbox.centurygame.com',
        rum_appkey: '91758f892f6c045cdeb2575ed86c1be9',
      }
    }[this.env]
    const t = Date.now().toString().substr(0,10)
    const signature = md5(`${rum_app_tag}:${t}:${rum_appkey}`)
    return `${rum_url}/log?tag=${rum_app_tag}&timestamp=${t}&num=1&signature=${signature}`
  }

  // 获取rum公共属性
  getCommonProperties() {
    return {
      sdk_version: 'web',
      ts: Date.now() * 1, // 当前时间戳
      // 系统os
      os: uaParser.getOS().name,
      // 系统版本
      os_version: uaParser.getOS().version,
      // 浏览器版本
      device: JSON.stringify({
        ...uaParser.getDevice(),
        UA: navigator.userAgent
      }, null, 2),
      // 浏览器可视窗口的高度，宽度, 浏览器语言
      carrier: JSON.stringify({
        width: window.innerWidth,
        height: window.innerHeight,
        lang: navigator.language || navigator.userLanguage,
      }, null, 2),
      // 设备Id
      target_user_id: this.getDeviceId(),
      // 访问地址
      http_url: window.location.href,
      // 网络情况
      http_status: this.getNetworkStatus(),
    }
  }

  // 获取设备id
  getDeviceId() {
    let deviceId
    deviceId = window.localStorage.getItem('rum_device_id')
    if (!deviceId) {
      deviceId = nanoid() + Date.now()
      window.localStorage.setItem('rum_device_id', deviceId)
    }
    return deviceId
  }

  // 获取浏览器网络情况
  getNetworkStatus() {
    const con = window.navigator.connetion
    if (con) {
      const net = {
        // 网络类型
        type: con.type,
        // 下载速度
        downlink: con.downlink,
        // 网络延时
        rtt: con.rtt,
      }
      return JSON.stringify(net)
    } else {
      return '浏览器版本过低，无法获取网络情况'
    }
  }
  /**
   * 设置自定义rum上报通用参数
   */
  setShareProperties(properties) {
    this.config_etag = typeof properties === 'object' ? JSON.stringify(properties) : ''
  }

  /**
   * 设置用户id
   */
  setUserId(id) {
    if (!id) return
    this.user_id = id
  }

  /**
   * 日志池新增日志并上报
   * @param {*} log
   */
  addLog(log) {
    const id = md5(JSON.stringify(log))
    const data = this.logPool.get(id);
    const currentTime = Date.now()

    console.log('log', log, id, data);

    // 日志池存在相同日志且已过期，或者不存在
    if ((!data) || (data && currentTime > data.expireTime)) {
      this.logPool.set(id, {
        expireTime: currentTime + this.interval
      })
      return this.baseReport(log)
    }
  }

  /**
   * 基础上报方法
   * @param {Object} param - 参数对象
   * @param {string} param.event - 上报事件名
   * @param {Object} [param.properties = {}] - 报告事件附带的属性描述（可选）
   */
  async baseReport({
    event,
    properties = {}
  }) {
    console.log('start log');
    try {
      await request({
        url: this.getReportUrl(),
        data: {
          app_id: this.app_id,
          data_version: '1.0',
          session_id: "unkown",
          event,
          properties: {
            ...this.getCommonProperties(),
            ...properties
          },
          user_id: this.user_id,
          rum_id: this.app_id,
          ts: Date.now() * 1,
        }
      })
    } catch (error) {
      console.log('rum', error);
    }
  }

  // 自定义日志上报
  report(event, properties, log_level) {
    const log =  JSON.stringify(properties, null, 2)
    this.baseReport({
      event,
      properties: {
        // 限制自定义的数据量大小在1M以内
        log: log.length < 1024 * 1024 ? log : log.substring(0, 1024 * 1024),
        log_level: log_level + '',
        config_etag: this.config_etag,
        module: '20121'
      }
    })
  }


  // 错误日志收集
  errorReport(error) {
    if (error instanceof Error) {
      // 格式化错误栈信息，抹平浏览器差异
      let errData = TraceKit.computeStackTrace(error)
      errData = {
        ...errData,
        // 截取前10个，避免错误栈过深导致单条rum日志体积过大
        stack: errData.stack.slice(0, 10)
      }
      this.addLog({
        event: 'error',
        properties: {
          req_id: md5(JSON.stringify(errData.stack[0].url)),
          call_stack_symbols: JSON.stringify(errData, null, 2)
        }
      })
    }
  }
}

export default Rum.getInstance()



// WEBPACK FOOTER //
// ./src/tool/rum.js